"use client";

import { FC, ReactNode, SyntheticEvent, useCallback, useContext, useMemo } from "react";
import {
    BonusPagesEnum,
    CasinoSectionsEnum,
    CasinoSectionsStyleEnum,
    IBannersWidgetProps,
    ILayoutsCasinoSection,
} from "@finbackoffice/fe-core";
import {
    AuthContext,
    CasinoFavoritesContext,
    CasinoScopesMap,
    getRandomArbitrary,
    useBonusPagesConfig,
    UseCasinoGamesHookOptions,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import classNames from "classnames";
import { SiteContentScopes, TranslationScopes } from "@finbackoffice/enums";
import dynamic from "next/dynamic";
import { IBannerResponse, ICasinoGame } from "@finbackoffice/clientbff-client";
import { getBannerSize } from "utils/helpers";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import PromoInfoPanel from "components/base/promo-info-panel/PromoInfoPanel";
import CasinoGames from "components/casino/widgets/games/CasinoGames";
import { BannerSliderSliderSettings } from "components/base/banners-slider/BannersSlider";
import PreloadComponent from "components/base/preload-component/PreloadComponent";
import Loading from "components/base/loading/Loading";
import styles from "../bonuses.module.sass";

const BannersSlider = dynamic(() => import("components/base/banners-slider/BannersSlider"), {
    loading: () => <PreloadComponent style={{ marginBottom: 10, aspectRatio: "auto 800 / 360" }} />,
    ssr: false,
});

const TopWinners = dynamic(() => import("components/casino/widgets/top-winners/TopWinners"), {
    // loading: () => <PreloadComponent style={{ height: 281 }} />,
    ssr: false,
});

type IProps = {
    headingIcon?: ReactNode;
    casinoGame?: ICasinoGame | null;
    banners: IBannerResponse[];
    translationScope?: TranslationScopes;
    promoPageName: BonusPagesEnum;
    showHeader?: boolean;
};

const PromoContent: FC<IProps> = ({
    casinoGame,
    headingIcon,
    banners,
    translationScope,
    promoPageName,
    showHeader = true,
}) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const { isUserLoggedIn } = useContext(AuthContext);
    const { handleFavoriteClick, favoriteGamesState, isFavorite } =
        useContext(CasinoFavoritesContext);

    const bonusPagesConfig = useBonusPagesConfig();

    const isCasinoGame = casinoGame !== undefined;
    const gameId = casinoGame?.id || "";
    const pageConfig = bonusPagesConfig.find((config) => config.name === promoPageName) || null;
    const topBannersWidgetIndex = useMemo(
        () =>
            pageConfig?.widgets?.findIndex((widget) => widget.name === CasinoSectionsEnum.Banners),
        [pageConfig?.widgets],
    );

    const topBannersMemo = useMemo(
        () => banners.filter((banner) => banner.tags.includes(SiteContentScopes.TopBanner)),
        [banners],
    );

    const bottomBannersMemo = useMemo(
        () => banners.filter((banner) => banner.tags.includes(SiteContentScopes.BottomBanner)),
        [banners],
    );
    const randomNumber = useMemo(() => getRandomArbitrary(500, 1000), []);

    const getBannerSettings = useCallback(
        (
            sectionConfig: IBannersWidgetProps<CasinoSectionsEnum.Banners>,
        ): BannerSliderSliderSettings => {
            const result: BannerSliderSliderSettings = { options: {}, autoplay: {} };

            if (sectionConfig.sliderSpeed) {
                result.autoplay.delay = sectionConfig.sliderSpeed;
            }

            if (sectionConfig.transitionSpeed) {
                result.options.duration = sectionConfig.transitionSpeed;
            }

            return result;
        },
        [],
    );

    const renderHeadingIcon = useMemo(() => {
        if (headingIcon) {
            return headingIcon;
        } else if (isCasinoGame) {
            return gameId ? (
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.Bonuses}/bonuses-${gameId?.replace("-mobile", "")}.svg`} // TODO gameID
                    className="svg-icon"
                    loader
                />
            ) : (
                <Loading />
            );
        } else {
            return (
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.Bonuses}/bonuses-promotions.svg`}
                    className="svg-icon"
                />
            );
        }
    }, [gameId, headingIcon, isCasinoGame]);

    const renderHeader = useMemo(
        () => (
            <h1 className={styles.header} data-testid="bonus-promo-heading">
                {renderHeadingIcon}
                <Translate
                    tid="bonuses_promo_heading"
                    namespace={translationScope}
                    replace={{ app_name: COMMON_SITE_CONFIGS.appName }}
                />
            </h1>
        ),
        [COMMON_SITE_CONFIGS.appName, renderHeadingIcon, translationScope],
    );

    const onFavoriteClick = useCallback(
        (e: SyntheticEvent) => {
            e.stopPropagation();
            if (!favoriteGamesState.loading) {
                void handleFavoriteClick(gameId);
            }
        },
        [favoriteGamesState.loading, gameId, handleFavoriteClick],
    );

    const renderInfoPanelHeader = useMemo(() => {
        if (showHeader) {
            if (!isCasinoGame) return null;

            return (
                <>
                    {gameId ? (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.Bonuses}/bonuses-${gameId?.replace("-mobile", "")}-logo.svg`}
                            loader
                        />
                    ) : (
                        <Loading />
                    )}

                    {isUserLoggedIn && (
                        <i
                            className={classNames(
                                styles.star,
                                isFavorite(gameId) && styles.starred,
                            )}
                            onClick={onFavoriteClick}
                        />
                    )}
                    <span className={styles.users}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/user.svg`}
                            className="svg-icon"
                            loader
                        />
                        {casinoGame ? randomNumber : <Loading />}
                    </span>
                    {casinoGame && (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Providers}/${casinoGame.category
                                .replaceAll(" ", "")
                                .toLowerCase()}.svg`}
                            className={classNames("svg-icon", styles.providerLogo)}
                        />
                    )}
                </>
            );
        }
        return (
            <h1 className={styles.promoHeading} data-testid="bonus-promo-heading">
                {renderHeadingIcon}
                <Translate
                    tid="bonuses_promo_heading"
                    namespace={translationScope}
                    replace={{ app_name: COMMON_SITE_CONFIGS.appName }}
                />
            </h1>
        );
    }, [
        COMMON_SITE_CONFIGS.appName,
        casinoGame,
        gameId,
        isCasinoGame,
        isFavorite,
        isUserLoggedIn,
        onFavoriteClick,
        randomNumber,
        renderHeadingIcon,
        showHeader,
        translationScope,
    ]);

    const renderSection = useCallback(
        (section: ILayoutsCasinoSection, index: number) => {
            if (section.enable) {
                switch (section.name) {
                    case CasinoSectionsEnum.Banners: {
                        const banners =
                            index === topBannersWidgetIndex ? topBannersMemo : bottomBannersMemo;

                        const imgProps = section.size
                            ? getBannerSize(section.size)
                            : {
                                  width: 800,
                                  height: 360,
                              };

                        const casinoGamePromoOptions = casinoGame
                            ? {
                                  forFunSupport:
                                      (index === topBannersWidgetIndex &&
                                          casinoGame.play_for_fun_supported) ||
                                      false,
                              }
                            : undefined;

                        if (!banners.length) return null;

                        return (
                            <BannersSlider
                                key={index}
                                banners={banners}
                                wrapperClassName={styles.bannerContainer}
                                sliderSettings={getBannerSettings(section)}
                                imgProps={imgProps}
                                casinoGamePromo={casinoGamePromoOptions}
                            />
                        );
                    }
                    case CasinoSectionsEnum.Slots:
                    case CasinoSectionsEnum.LiveCasino: {
                        const gameTypes = section.type || CasinoScopesMap[section.name];
                        const commonOptions: UseCasinoGamesHookOptions = {
                            megaways: section.prop === "megaways",
                            bonusbuy: section.prop === "bonusbuy",
                            has_jackpot: section.prop === "has_jackpot",
                        };

                        if (section.search) {
                            commonOptions.gameName = section.search;
                        }

                        const getDefaultPagination = () => {
                            switch (section.style) {
                                case CasinoSectionsStyleEnum.Mosaic:
                                    return { items_per_page: 36 };
                                default:
                                    return { items_per_page: 30 };
                            }
                        };

                        return (
                            <CasinoGames
                                id={section.id}
                                key={index}
                                style={section.style}
                                tag={section.tag}
                                gameTypes={gameTypes}
                                provider={section.provider || "All"}
                                options={{
                                    defaultPagination: getDefaultPagination(),
                                    ...commonOptions,
                                }}
                                href={section.href}
                                backImg={
                                    section.style === CasinoSectionsStyleEnum.RoundRow
                                        ? section.backImg
                                        : undefined
                                }
                                imgSrc={section.imgSrc}
                                imgFolder={COMMON_SITE_CONFIGS.casinoImg}
                            />
                        );
                    }
                    case CasinoSectionsEnum.Winners:
                        return (
                            <div key={index} className={styles.topWinners}>
                                <ErrorBoundary key={index} name={TopWinners.displayName}>
                                    <TopWinners config={section.config} />
                                </ErrorBoundary>
                            </div>
                        );
                    case CasinoSectionsEnum.PromoText:
                        return (
                            <PromoInfoPanel
                                key={index}
                                renderHeader={renderInfoPanelHeader}
                                wrapperClassname={casinoGame ? styles.casinoGameInfo : undefined}
                                text={
                                    <Translate
                                        tid="bonuses_promo_content"
                                        dangerous
                                        namespace={translationScope}
                                        replace={{ app_name: COMMON_SITE_CONFIGS.appName }}>
                                        <div />
                                    </Translate>
                                }
                            />
                        );
                    default:
                        return null;
                }
            }
            return null;
        },
        [
            COMMON_SITE_CONFIGS.appName,
            COMMON_SITE_CONFIGS.casinoImg,
            bottomBannersMemo,
            casinoGame,
            getBannerSettings,
            renderInfoPanelHeader,
            topBannersMemo,
            topBannersWidgetIndex,
            translationScope,
        ],
    );

    return (
        <div className={classNames(styles.content, styles.bonusesAviator)}>
            {showHeader && renderHeader}
            {pageConfig?.widgets?.map(renderSection)}
        </div>
    );
};

export default PromoContent;
